import { apiClient } from '@/auth';

/** Maps to swagger.yaml:14438 PatientSubscriptionResponse. */
export type PatientSubscription = {
  id: number;
  patient_id: number;
  hospital_id?: number;
  clinic_id?: number;
  name: SubscriptionName;
  type: SubscriptionType;
  start_date: string;
  /** REQUIRED on the server response — open-ended subs aren't supported. */
  expiration_date: string;
  report_date?: string;
  subscription_length?: number | null;
  is_logging_complete?: boolean;
  is_complete?: boolean;
};

export const SUBSCRIPTION_PACKAGE_NAMES = [
  'three-day',
  'five-day',
  'seven-day',
  'ten-day',
  '14-day',
  '21-day',
  '28-day',
  'one-month',
  'cgm14',
  'custom',
] as const;
export type SubscriptionName = (typeof SUBSCRIPTION_PACKAGE_NAMES)[number];

export const SUBSCRIPTION_TYPES = [
  'single',
  'weekly',
  'monthly',
  'bi-weekly',
  '4-weeks',
  'custom',
] as const;
export type SubscriptionType = (typeof SUBSCRIPTION_TYPES)[number];

/**
 * Body for POST `/hospital/{hospital_id}/patients/{patient_id}/subscription`
 * (v1 `appApiSubscriptionApiPostPatientSubscription` /
 * `CreatePatientSubscriptionRequest`). `expiration_date` is required
 * server-side. `start_date` belongs in this body, not the query string.
 *
 * Do not POST `/patients/{id}/subscriptions` — that operation requires
 * `start_date` as a query parameter and ignores this body (400
 * "Missing query parameter 'start_date'").
 */
export type CreateSubscriptionInput = {
  name: SubscriptionName;
  type: SubscriptionType;
  start_date: string;
  expiration_date: string;
  report_date?: string;
  clinic_id?: number;
};

/**
 * Body for PUT `/hospital/{hospital_id}/patients/{patient_id}/subscription/{patient_subscription_id}`
 * (MNT swagger:1746, `UpdatePatientSubscriptionRequest`). Same required fields
 * as create.
 *
 * NOTE: Both update and delete live under the **hospital-prefixed** path with
 * `subscription` (singular). The plural-without-hospital variant `/patients/{id}/subscriptions/{id}`
 * doesn't exist in the MNT swagger — V2 had this wrong before.
 */
export type UpdateSubscriptionInput = {
  name: SubscriptionName;
  type: SubscriptionType;
  start_date: string;
  expiration_date: string;
  report_date?: string;
};

export const subscriptionsApi = {
  async list(patientId: number) {
    const res = await apiClient.get<PatientSubscription[]>(`/patients/${patientId}/subscriptions`);
    return Array.isArray(res.data) ? res.data : [];
  },
  /**
   * Create a logging period. Mirrors v1's `patientService.addSubscription`
   * (clinician/src/services/patient.ts:810): hospital-prefixed, singular
   * `subscription`, JSON body. Same path family as update and delete.
   */
  async create(args: { hospitalId: number; patientId: number; body: CreateSubscriptionInput }) {
    const { hospitalId, patientId, body } = args;
    const res = await apiClient.post<PatientSubscription>(
      `/hospital/${hospitalId}/patients/${patientId}/subscription`,
      body,
    );
    return res.data;
  },
  async update(args: {
    hospitalId: number;
    patientId: number;
    subscriptionId: number;
    body: UpdateSubscriptionInput;
  }) {
    const { hospitalId, patientId, subscriptionId, body } = args;
    const res = await apiClient.put<PatientSubscription>(
      `/hospital/${hospitalId}/patients/${patientId}/subscription/${subscriptionId}`,
      body,
    );
    return res.data;
  },
  /**
   * Cancel = DELETE the subscription record. Mirrors v1's
   * `patientService.deleteSubscription` (clinician/src/services/patient.ts:910).
   * The server returns 200 (not 204) per swagger:1779.
   */
  async cancel(args: { hospitalId: number; patientId: number; subscriptionId: number }) {
    const { hospitalId, patientId, subscriptionId } = args;
    await apiClient.delete(
      `/hospital/${hospitalId}/patients/${patientId}/subscription/${subscriptionId}`,
    );
  },
};
